// VIN Decoder - Handles VIN decoding and vehicle search
class VINDecoder {
  constructor() {
    this.vinInput = document.getElementById('vin-search');
    this.vinButton = document.getElementById('vin-search-button');

    // NHTSA VIN Decoder API (free, no API key required)
    this.vinApiUrl = 'https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin';

    this.init();
  }

  init() {
    if (!this.vinInput || !this.vinButton) return;

    // Load VIN from URL if exists
    const urlParams = new URLSearchParams(window.location.search);
    const vin = urlParams.get('vin');
    if (vin) {
      this.vinInput.value = vin;
    }

    // Button click handler
    this.vinButton.addEventListener('click', () => this.decodeVIN());

    // Enter key handler
    this.vinInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.decodeVIN();
      }
    });

    // Real-time VIN validation
    this.vinInput.addEventListener('input', (e) => {
      // Convert to uppercase and remove invalid characters
      e.target.value = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/gi, '');
    });
  }

  validateVIN(vin) {
    // VIN must be exactly 17 characters
    if (vin.length !== 17) {
      return { valid: false, error: 'VIN must be exactly 17 characters' };
    }

    // VIN cannot contain I, O, or Q
    if (/[IOQ]/i.test(vin)) {
      return { valid: false, error: 'VIN cannot contain letters I, O, or Q' };
    }

    return { valid: true };
  }

  async decodeVIN() {
    const vin = this.vinInput.value.trim();

    if (!vin) {
      alert('Please enter a VIN number');
      return;
    }

    // Validate VIN format
    const validation = this.validateVIN(vin);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    // Show loading state
    this.vinButton.disabled = true;
    this.vinButton.textContent = 'Decoding...';

    try {
      const vehicleData = await this.fetchVINData(vin);

      if (vehicleData.success) {
        // Trigger vehicle search with decoded data
        this.triggerVehicleSearch(vehicleData, vin);
      } else {
        alert(vehicleData.error || 'Unable to decode VIN. Please check the VIN and try again.');
      }
    } catch (error) {
      console.error('VIN decode error:', error);
      alert('An error occurred while decoding VIN. Please try again.');
    } finally {
      // Restore button state
      this.vinButton.disabled = false;
      this.vinButton.textContent = 'Decode VIN';
    }
  }

  async fetchVINData(vin) {
    try {
      const url = `${this.vinApiUrl}/${vin}?format=json`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Parse the response
      if (data.Results && Array.isArray(data.Results)) {
        const results = data.Results;

        // Extract relevant fields
        const year = this.getField(results, 'Model Year');
        const make = this.getField(results, 'Make');
        const model = this.getField(results, 'Model');

        // Check if we got valid data
        if (!year || !make || !model) {
          return {
            success: false,
            error: 'Unable to decode VIN. The VIN may be invalid or not found in the database.'
          };
        }

        return {
          success: true,
          year: year,
          make: make,
          model: model,
          // Additional data that might be useful
          trim: this.getField(results, 'Trim'),
          engineModel: this.getField(results, 'Engine Model'),
          engineCylinders: this.getField(results, 'Engine Number of Cylinders'),
          vehicleType: this.getField(results, 'Vehicle Type'),
          bodyClass: this.getField(results, 'Body Class'),
        };
      }

      return {
        success: false,
        error: 'Invalid response from VIN decoder service'
      };
    } catch (error) {
      console.error('Fetch VIN data error:', error);
      throw error;
    }
  }

  getField(results, fieldName) {
    const field = results.find(item => item.Variable === fieldName);
    return field && field.Value ? field.Value : null;
  }

  triggerVehicleSearch(vehicleData, vin) {
    const { year, make, model } = vehicleData;

    console.log('[VIN Decoder] Decoded vehicle:', vehicleData);

    // Check if we're on the find-my-part page
    const isFindMyPartPage = window.location.pathname.includes('/find-my-part');

    if (isFindMyPartPage) {
      // Clear other search fields (YMM selectors and part number)
      if (typeof window.clearAllSearchFields === 'function') {
        window.clearAllSearchFields('vin');
      }

      // Update URL params without reload - only keep VIN
      const url = new URL(window.location.href);

      // Remove all other search parameters
      url.searchParams.delete('year');
      url.searchParams.delete('make');
      url.searchParams.delete('model');
      url.searchParams.delete('number');

      // Set only VIN parameter
      url.searchParams.set('vin', vin);

      // Update URL without reload
      window.history.pushState({}, '', url);

      // Update body class to show results
      document.body.classList.add('has-ymm-selection');
      document.body.classList.remove('show-ymm-selector');

      // Update display text
      const displayElement = document.getElementById('ymm-display-text');
      if (displayElement) {
        displayElement.textContent = `${year} ${make} ${model} (VIN: ${vin})`;
      }

      // Trigger search event for FindMyPartSearch to pick up
      const searchEvent = new CustomEvent('vin-search', {
        detail: {
          year: year,
          make: make,
          model: model,
          vin: vin,
          source: 'vin-decoder'
        },
        bubbles: true,
      });
      window.dispatchEvent(searchEvent);
    } else {
      // Redirect to find-my-part page with only VIN parameter
      const findMyPartUrl = `/pages/find-my-part?vin=${encodeURIComponent(vin)}`;
      window.location.href = findMyPartUrl;
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VINDecoder();
  });
} else {
  new VINDecoder();
}
